import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'
import { DocumentTextIcon, DocumentChartBarIcon, PresentationChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'

/**
 * Office file renderer for .docx / .pptx / .xlsx output from office-generation.
 *
 * Office files don't have a native browser viewer — renders a download card
 * with the file type, size if available, and a prominent download button.
 * Optional thumbnail if the agent returned one.
 */

type OfficeFormat = 'docx' | 'pptx' | 'xlsx' | 'unknown'

interface OfficeFile {
  url: string
  format: OfficeFormat
  filename?: string
  sizeBytes?: number
  slideCount?: number
  pageCount?: number
  thumbnailUrl?: string
}

const FORMAT_META: Record<OfficeFormat, { label: string; Icon: any; color: string }> = {
  docx: { label: 'Word Document', Icon: DocumentTextIcon, color: '#2B579A' },
  pptx: { label: 'PowerPoint', Icon: PresentationChartBarIcon, color: '#D24726' },
  xlsx: { label: 'Excel Workbook', Icon: DocumentChartBarIcon, color: '#217346' },
  unknown: { label: 'Office File', Icon: DocumentTextIcon, color: '#666' },
}

function detectFormat(url?: string, format?: string): OfficeFormat {
  const src = (format || url || '').toLowerCase()
  if (src.includes('docx')) return 'docx'
  if (src.includes('pptx')) return 'pptx'
  if (src.includes('xlsx')) return 'xlsx'
  return 'unknown'
}

function extractFile(result: any): OfficeFile | null {
  const r = result || {}
  const ad = r.agent_data || {}
  for (const src of [r, ad]) {
    const url = src.file_url || src.pptx_url || src.docx_url || src.xlsx_url
    if (url) {
      return {
        url,
        format: detectFormat(url, src.format),
        filename: src.filename,
        sizeBytes: src.file_size_bytes ?? src.size_bytes,
        slideCount: src.slide_count ?? src.slides,
        pageCount: src.page_count ?? src.pages,
        thumbnailUrl: src.thumbnail_url ?? src.thumbnail,
      }
    }
  }
  return null
}

function humanSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(0)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

export default function OfficeFileRenderer(props: AgentOutputProps) {
  const file = useMemo(() => extractFile(props.result), [props.result])

  if (!file) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No Office file URL found in the agent result.
      </div>
    )
  }

  const meta = FORMAT_META[file.format]
  const size = humanSize(file.sizeBytes)

  return (
    <div
      className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card max-w-2xl"
      style={{ borderLeft: `4px solid ${meta.color}` }}
    >
      {file.thumbnailUrl ? (
        <img
          src={file.thumbnailUrl}
          alt=""
          className="w-20 h-20 object-cover rounded border border-border"
        />
      ) : (
        <div
          className="w-20 h-20 rounded border border-border flex items-center justify-center"
          style={{ background: `${meta.color}15` }}
        >
          <meta.Icon className="w-10 h-10" style={{ color: meta.color }} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {file.filename || `${meta.label.toLowerCase().replace(' ', '-')}.${file.format}`}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {meta.label}
          {size && ` · ${size}`}
          {file.slideCount && ` · ${file.slideCount} slides`}
          {file.pageCount && ` · ${file.pageCount} pages`}
        </div>
      </div>

      <a
        href={file.url}
        download={file.filename}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
        Download
      </a>
    </div>
  )
}
